import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Eye, Ban, Trash2, X, Unlock, ArrowLeft, ArrowRight } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Table from "../../base-component/Table/Table";
import { coachData } from "../../base-component/Data";
import Pagination from "../../base-component/Pagination/Pagination";
import Dialog from "../../base-component/Dialog/Dialog";
import CustomDatePicker from "../../base-component/CustomDatePicker/CustomDatePicker";
import moment from "moment";
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { userListApi } from "../../Services/ApiServices";
import { IMG_URL } from "../../Services/Api";
import dummy from "../../assets/Images/dummy.jpeg"
import { blockUnblockUserApi } from '../../Services/ApiServices';
import toast from 'react-hot-toast';

const Coach = () => {
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const itemsPerPage = 10;
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [coachList, setCoachList] = useState([]);
  const [blockedReason, setBlockedReason] = useState("");

  const handleView = (coachId) => {
    navigate(`/coachdetail/${coachId}`);
  };

  const handleBlockClick = (coach) => {
    setSelectedCoach(coach);
    setIsBlockDialogOpen(true);
  };

  const handleDelete = (coach) => {
    setSelectedCoach(coach);
    setIsDeleteDialogOpen(true);
  };

  const blockValidationSchema = Yup.object().shape({
    blocked_reason: Yup.string()
      .when('$isBlocked', {
        is: false,
        then: (schema) => schema
          .required("Blocked reason is required")
          .min(10, "Blocked reason must be at least 10 characters")
          .max(500, "Blocked reason must not exceed 500 characters"),
        otherwise: (schema) => schema.notRequired()
      })
      .trim(),
  });

  const handleBlockConfirm = (values) => {
    if (!selectedCoach) return;
    
    const isBlocked = selectedCoach.is_blocked || selectedCoach.status === 'blocked';
    
    let params = {
      user_id: selectedCoach.id,
    };
    
    if (!isBlocked && values.blocked_reason) {
      params.blocked_reason = values.blocked_reason.trim();
    }
    
    blockUnblockUserApi(params)
    .then(res => {
      console.log(res.data);
      toast.success(res.data.message);
      setIsBlockDialogOpen(false);
      setSelectedCoach(null);
      setBlockedReason("");
      handleCoachList();
    })
    .catch(err => {
      console.log(err);
      toast.error(err.response.data.message);
    })
  };

  const handleDeleteConfirm = () => {
    console.log("Delete coach:", selectedCoach);
    setIsDeleteDialogOpen(false);
    setSelectedCoach(null);
  };

  const filteredCoaches = useMemo(() => {
    let filtered = [...coachData];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((coach) => {
        return (
          coach.firstName?.toLowerCase().includes(searchLower) ||
          coach.lastName?.toLowerCase().includes(searchLower) ||
          coach.phone?.toLowerCase().includes(searchLower) ||
          coach.country?.toLowerCase().includes(searchLower) ||
          coach.city?.toLowerCase().includes(searchLower)
        );
      });
    }

    if (fromDate || toDate) {
      filtered = filtered.filter((coach) => {
        const dateField = coach.createdAt || coach.coachingSince;
        if (!dateField) return false;
        
        const coachDate = moment(dateField, "YYYY-MM-DD");
        
        if (fromDate && toDate) {
          return coachDate.isSameOrAfter(moment(fromDate).startOf("day")) && 
                 coachDate.isSameOrBefore(moment(toDate).endOf("day"));
        } else if (fromDate) {
          return coachDate.isSameOrAfter(moment(fromDate).startOf("day"));
        } else if (toDate) {
          return coachDate.isSameOrBefore(moment(toDate).endOf("day"));
        }
        
        return true;
      });
    }

    return filtered;
  }, [searchTerm, fromDate, toDate]);

  const startIndex = (pageNo - 1) * itemsPerPage;

  const handlePageChange = (page) => {
    setPageNo(page);
  };

  const renderPaginationButtons = () => {
    const maxPagesToShow = 3;
    const buttons = [];
    const startPage = Math.max(1, pageNo - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(pageCount, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={
            i === pageNo
              ? "bg-[#2C5BA7] text-white w-10 h-10 rounded-lg font-medium text-xl"
              : "text-[#4B4B4B] w-10 h-10 rounded-lg font-medium text-xl"
          }
        >
          {i}
        </button>
      );
    }
    return buttons;
  };
  
  const handleCoachList = () => {
    setIsLoading(true);
    userListApi({
      page: pageNo,
      limit: itemsPerPage,
      fromDate: fromDate,
      toDate: toDate,
      user_role: "coach",
    })
    .then(res => {
      console.log(res.data.data);
      setCoachList(res.data.data);
      const totalPagesCount = res?.data?.total_pages || res?.data?.totalPages || res?.data?.last_page || 1;
      setPageCount(totalPagesCount);
      setIsLoading(false);
    })
    .catch(err => {
      console.log(err);
      setCoachList([]);
      setPageCount(1);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    handleCoachList();
  }, [pageNo, fromDate, toDate, searchTerm]);
  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <p className="text-xl p-0 m-0 font-bold text-gray-900 tracking-tight">Coach</p>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 w-full sm:w-auto">
          <div className="w-full sm:w-48 md:w-56 lg:w-52 xl:w-64">
            <CustomDatePicker
              placeholder="From Date"
              value={fromDate}
              isAllday={true}
              onChange={(date) => setFromDate(date)}
              innerClassName="rounded-xl px-4 h-11 shadow-sm outline-none border border-[#F2F4F7]"
              isType="daily"
              className="w-full"
            />
          </div>
          <div className="w-full sm:w-48 md:w-56 lg:w-52 xl:w-64">
            <CustomDatePicker
              placeholder="To Date"
              value={toDate}
              isAllday={true}
              onChange={(date) => setToDate(date)}
              innerClassName="rounded-xl px-4 h-11 shadow-sm outline-none border border-[#F2F4F7]"
              isType="daily"
              className="w-full"
            />
          </div>
          <input
            type="text"
            placeholder="Search coaches..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border rounded-lg px-4 py-2 w-full sm:w-64 focus:outline-none"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <Table>
          <Table.Thead variant="light">
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>PROFILE</Table.Th>
              <Table.Th>PHONE NUMBER</Table.Th>
              <Table.Th>COUNTRY</Table.Th>
              <Table.Th>CITY</Table.Th>
              <Table.Th>CREATED AT</Table.Th>
              <Table.Th className="text-center">ACTIONS</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {coachList.map((coach, index) => (
              <Table.Tr key={coach.id}>
                <Table.Td className="font-semibold text-slate-700">
                  {startIndex + index + 1}
                </Table.Td>

                <Table.Td>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden border flex-shrink-0">
                      <Zoom>
                        <img
                          src={ coach.profile_image ? IMG_URL + coach.profile_image : dummy}
                          alt={coach.firstname + coach.lastname}
                          onError={(e) => {
                            e.target.src = dummy;
                          }}
                          className="w-full h-full object-cover"
                        />
                      </Zoom>
                    </div>
                    <span className="font-medium text-slate-800">
                      {coach.firstname} {coach.lastname}
                    </span>
                  </div>
                </Table.Td>

                <Table.Td className="text-slate-600">{coach.country_code} {coach.phone_number || "-"}</Table.Td>

                <Table.Td className="text-slate-600">{coach.country || "-"}</Table.Td>

                <Table.Td className="text-slate-600">{coach.city || "-"}</Table.Td>

                <Table.Td className="text-slate-600">{moment(coach.created_at).format("DD-MM-YYYY h:mm A") || "—"}</Table.Td>

                <Table.Td>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => handleView(coach.id)}
                      className="p-2 rounded-lg transition border"
                      style={{ 
                        borderColor: '#6B9FE8',
                        backgroundColor: '#E8F2FC',
                        color: '#6B9FE8'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#2C5BA7';
                        e.target.style.backgroundColor = '#D4E4F7';
                        e.target.style.color = '#2C5BA7';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#6B9FE8';
                        e.target.style.backgroundColor = '#E8F2FC';
                        e.target.style.color = '#6B9FE8';
                      }}
                    >
                      <Eye size={16} />
                    </button>

                    <button 
                      onClick={() => handleBlockClick(coach)}
                      className="p-2 rounded-lg transition border"
                      style={{ 
                        borderColor: coach.is_blocked || coach.status === 'blocked' ? '#10B981' : '#FB923C',
                        backgroundColor: coach.is_blocked || coach.status === 'blocked' ? '#D1FAE5' : '#FFF4ED',
                        color: coach.is_blocked || coach.status === 'blocked' ? '#10B981' : '#FB923C'
                      }}
                      onMouseEnter={(e) => {
                        if (coach.is_blocked || coach.status === 'blocked') {
                          e.target.style.borderColor = '#059669';
                          e.target.style.backgroundColor = '#A7F3D0';
                          e.target.style.color = '#059669';
                        } else {
                          e.target.style.borderColor = '#f97316';
                          e.target.style.backgroundColor = '#FEE4D6';
                          e.target.style.color = '#f97316';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (coach.is_blocked || coach.status === 'blocked') {
                          e.target.style.borderColor = '#10B981';
                          e.target.style.backgroundColor = '#D1FAE5';
                          e.target.style.color = '#10B981';
                        } else {
                          e.target.style.borderColor = '#FB923C';
                          e.target.style.backgroundColor = '#FFF4ED';
                          e.target.style.color = '#FB923C';
                        }
                      }}
                    >
                      {coach.is_blocked || coach.status === 'blocked' ? (
                        <Unlock size={16} />
                      ) : (
                        <Ban size={16} />
                      )}
                    </button>

                    <button 
                      onClick={() => handleDelete(coach)}
                      className="p-2 rounded-lg transition border"
                      style={{ 
                        borderColor: '#F87171',
                        backgroundColor: '#FEF2F2',
                        color: '#F87171'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#ef4444';
                        e.target.style.backgroundColor = '#FEE2E2';
                        e.target.style.color = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#F87171';
                        e.target.style.backgroundColor = '#FEF2F2';
                        e.target.style.color = '#F87171';
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </div>

      {coachList.length > 0 && (
        <div className="flex mt-5 items-center col-span-12 justify-between">
          <Pagination>
            <Pagination.Link>
              <button
                className='rounded-lg p-1.5 hover:bg-[#101828] hover:text-white transition-all duration-300 cursor-pointer font-bold text-base box border border-[#EDEDED] disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={handlePageChange.bind(null, pageNo - 1)}
                disabled={pageNo == 1}
              >
                <ArrowLeft />
              </button>
            </Pagination.Link>
            <div className="flex items-center">
              {renderPaginationButtons()}
            </div>
            <Pagination.Link>
              <button
                className='rounded-lg p-1.5 hover:bg-[#101828] hover:text-white transition-all duration-300 cursor-pointer font-bold text-base box border border-[#EDEDED] disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={handlePageChange.bind(null, pageNo + 1)}
                disabled={pageNo === pageCount}
              >
                <ArrowRight />
              </button>
            </Pagination.Link>
          </Pagination>
        </div>
      )}

      <Dialog
        open={isBlockDialogOpen}
        onClose={() => {
          setIsBlockDialogOpen(false);
          setSelectedCoach(null);
          setBlockedReason("");
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="px-4 py-4 text-center relative" style={{ backgroundColor: '#FFF4ED' }}>
              <button
                onClick={() => {
                  setIsBlockDialogOpen(false);
                  setSelectedCoach(null);
                  setBlockedReason("");
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>

              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-full flex items-center justify-center">
                  <Ban size={56} className="text-[#FB923C]" />
                </div>
              </div>
            </div>

            <div className="bg-white px-6 py-8 text-center w-full">
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-4">
                {selectedCoach && (selectedCoach.is_blocked || selectedCoach.status === 'blocked') ? 'Unblock Coach?' : 'Block Coach?'}
              </div>

              <Dialog.Description className="md:text-base text-sm text-gray-600 mb-6 leading-relaxed">
                {selectedCoach && (selectedCoach.is_blocked || selectedCoach.status === 'blocked')
                  ? `Are you sure you want to unblock ${selectedCoach.firstname} ${selectedCoach.lastname}? They will be able to access their account again.`
                  : `Are you sure you want to block ${selectedCoach ? `${selectedCoach.firstname} ${selectedCoach.lastname}` : 'this coach'}? They will not be able to access their account.`
                }
              </Dialog.Description>

              <Formik
                initialValues={{ blocked_reason: blockedReason }}
                enableReinitialize
                validationSchema={blockValidationSchema}
                validationContext={{ isBlocked: selectedCoach && (selectedCoach.is_blocked || selectedCoach.status === 'blocked') }}
                onSubmit={(values) => handleBlockConfirm(values)}
              >
                {({ errors, touched }) => {
                  const isBlocked = selectedCoach && (selectedCoach.is_blocked || selectedCoach.status === 'blocked');
                  return (
                    <Form>
                      {!isBlocked && (
                        <div className="mb-4 text-left">
                          <Field
                            as="textarea"
                            name="blocked_reason"
                            placeholder="Enter blocked reason"
                            className={`w-full border rounded-lg px-4 py-2 h-24 resize-none focus:outline-none focus:ring-2 focus:ring-[#FB923C] focus:border-transparent ${
                              errors.blocked_reason && touched.blocked_reason
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          <ErrorMessage
                            name="blocked_reason"
                            component="div"
                            className="text-red-500 text-sm mt-1 text-left"
                          />
                        </div>
                      )}
                      <button
                        type="submit"
                        className={`px-6 h-12 shadow cursor-pointer w-full text-white font-medium rounded-xl transition-colors ${
                          isBlocked 
                            ? 'bg-[#10B981] hover:bg-[#059669]' 
                            : 'bg-[#FB923C] hover:bg-[#f97316]'
                        }`}
                      >
                        {isBlocked ? 'Yes, Unblock' : 'Yes, Block'}
                      </button>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>
                
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedCoach(null);
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="px-4 py-4 text-center relative" style={{ backgroundColor: '#FEF2F2' }}>
              <button
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setSelectedCoach(null);
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>

              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-full flex items-center justify-center">
                  <Trash2 size={56} className="text-[#F87171]" />
                </div>
              </div>
            </div>

            <div className="bg-white px-6 py-8 text-center w-full">
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-4">
                Delete Coach?
              </div>

              <Dialog.Description className="md:text-base text-sm text-gray-600 mb-8 leading-relaxed">
                Are you sure you want to delete {selectedCoach ? `${selectedCoach.firstName} ${selectedCoach.lastName}` : 'this coach'}? This action cannot be undone.
              </Dialog.Description>

              <button
                onClick={handleDeleteConfirm}
                className="px-6 h-12 shadow cursor-pointer bg-[#F87171] w-full text-white font-medium rounded-xl hover:bg-[#ef4444] transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>
    </>
  );
};

export default Coach;